<script setup lang="ts">
import { onMounted, computed, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'
import { usePaymentLink } from '@/composables/usePaymentLink'
import { signingToken } from '@/composables/useSigningLink'
import SignDocument from '@/views/SignDocument.vue'
import SignIn from '@/views/SignIn.vue'
import { AlertCircle, CheckCircle2, Clock, CreditCard, ExternalLink } from 'lucide-vue-next'
import DebugConsole from '@/components/DebugConsole.vue'
import CompanyShell from '@/components/shell/CompanyShell.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

const { t, te } = useI18n()

const auth = useAuthStore()
const route = useRoute()

/**
 * Render whatever the auth store put in `error`.
 *
 * Messages we author ourselves are i18n keys, so they translate. Anything that
 * came from the server or a thrown exception is already a sentence and has no
 * key — `te()` tells the two apart. Without this the store would have to reach
 * for i18n itself, which it cannot do outside a component scope.
 */
const authMessage = computed(() => {
  const message = auth.error
  if (!message) return ''
  return te(message) ? t(message) : message
})

const {
  isPaymentLink,
  state: paymentState,
  open: openPaymentLink,
} = usePaymentLink()

/**
 * Catch up after a payment link is paid.
 *
 * Only matters for a signed-in customer: they never see the confirmation
 * screen above (it renders only for the signed-out case, where Paddle's own
 * overlay is the only feedback there is), so for them the app behind the
 * overlay is what reports the outcome — and the entitlement is applied by the
 * webhook asynchronously, so whatever is on screen is stale the moment the
 * overlay closes. `useBilling` runs the same refresh after an in-app checkout;
 * a payment link is the one purchase path that does not go through it.
 */
watch(paymentState, (current) => {
  if (current !== 'completed' || !auth.isAuthenticated) return
  const dashboard = useDashboardStore()
  // On a short backoff, not once. The webhook usually lands within a second or
  // two, so a single immediate refresh reliably reads the state from *before*
  // the purchase and reports nothing changed. Unlike useBilling's poll there is
  // nothing to detect arrival against — a payment link can settle any kind of
  // transaction — so this is a fixed, bounded catch-up. refresh() de-dupes
  // concurrent callers, so overlapping with anything else is harmless.
  for (const delay of [0, 2000, 5000]) setTimeout(() => void dashboard.refresh(), delay)
})

onMounted(() => {
  // A signing link is for someone who is not a user of this portal at all —
  // often not of Lark either. Nothing else starts: no sign-in, no redirect to
  // Lark's OAuth screen, no checkout.
  if (signingToken) return
  // A Paddle payment link carries its own transaction and needs no Lark
  // session, so it starts alongside authentication rather than after it —
  // and still opens if authentication goes on to fail.
  if (isPaymentLink.value) void openPaymentLink()
  auth.initialize()
})
</script>

<template>
  <!-- E-signature link (#/sign/<token>) — see useSigningLink. Ahead of
       everything, the payment link included: the signer has no session and
       needs none, and the token alone decides what they may see. -->
  <SignDocument v-if="signingToken" :token="signingToken" />

  <!-- Paddle payment link (?_ptxn=...) — see usePaymentLink.
       Deliberately ahead of the Lark gate: the transaction is self-contained,
       so a customer who opened the emailed link in a desktop browser has to
       reach the checkout, not a "please open this in Lark" wall. Inside Lark
       the normal app renders instead and the overlay sits on top of it. -->
  <div v-else-if="isPaymentLink && !auth.isAuthenticated" class="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
    <header class="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
        <img src="/logo.svg" alt="AutomaDoc" class="h-8 w-8 sm:h-10 sm:w-10" />
        <span class="text-lg sm:text-xl font-bold text-gray-800 dark:text-gray-100">AutomaDoc</span>
      </div>
    </header>

    <div class="flex-1 flex items-center justify-center p-8">
      <div class="text-center max-w-md">
        <!-- While the overlay is up it *is* the interface; this is backdrop. -->
        <LoadingSpinner
          v-if="paymentState === 'opening' || paymentState === 'open'"
          :text="paymentState === 'open' ? t('paymentLinkOpen') : t('paymentLinkOpening')"
        />

        <template v-else-if="paymentState === 'completed'">
          <h1 class="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"><CheckCircle2 :size="20" class="shrink-0 text-green-600 dark:text-green-400" aria-hidden="true" />{{ t('paymentLinkCompleted') }}</h1>
          <p class="text-gray-600 dark:text-gray-400">{{ t('paymentLinkCompletedHint') }}</p>
        </template>

        <!-- Closed without paying. The URL parameter is long gone, so this
             button is the only way back to the checkout. -->
        <template v-else-if="paymentState === 'closed'">
          <h1 class="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"><CreditCard :size="20" class="shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />{{ t('paymentLinkClosed') }}</h1>
          <p class="text-gray-600 dark:text-gray-400 mb-4">{{ t('paymentLinkClosedHint') }}</p>
          <button
            @click="openPaymentLink()"
            class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <CreditCard :size="16" />
            {{ t('paymentLinkResume') }}
          </button>
        </template>

        <template v-else>
          <h1 class="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"><AlertCircle :size="20" class="shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />{{ t('paymentLinkError') }}</h1>
          <p class="text-gray-600 dark:text-gray-400">{{ t('paymentLinkErrorHint') }}</p>
        </template>

      </div>
    </div>
  </div>

  <!-- A page for someone with no account (the IT admin page): readable without any
       sign-in, once the store knows there is none (inside Lark it signs in silently, and
       the page is then an ordinary one in the shell below). -->
  <div v-else-if="route.meta.public && !auth.isAuthenticated && !auth.isLoading" class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <header class="border-b border-gray-200 bg-white dark:border-white/10 dark:bg-gray-800">
      <div class="mx-auto flex max-w-4xl items-center gap-2.5 px-4 py-3 sm:px-6 lg:px-8">
        <img src="/logo.svg" alt="" class="h-7 w-7" />
        <span class="text-base font-semibold text-gray-900 dark:text-gray-100">AutomaDoc</span>
        <a href="#/" class="ml-auto text-sm font-medium text-blue-700 hover:underline dark:text-blue-300">{{ t('navSignIn') }}</a>
      </div>
    </header>
    <RouterView />
  </div>

  <!-- Loading state -->
  <div v-else-if="auth.isLoading" class="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <LoadingSpinner :text="t('authenticating')" />
  </div>

  <!-- Signed out in a browser: the sign-in chooser (Microsoft, Google, Lark,
       email — whatever this environment has on). Inside the Lark client the
       store signs in silently and this never shows. -->
  <SignIn v-else-if="auth.state === 'not_in_lark'" />

  <!-- App not installed -->
  <div v-else-if="auth.state === 'app_not_installed'" class="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
    <div class="text-center max-w-md">
      <h1 class="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"><AlertCircle :size="20" class="shrink-0 text-orange-600 dark:text-orange-400" aria-hidden="true" />{{ t('appNotInstalled') }}</h1>
      <p class="text-gray-600 dark:text-gray-400 mb-6">{{ t('appNotInstalledMessage') }}</p>
      <a
        href="https://app.larksuite.com/app/cli_a9ba5c534778de1c"
        target="_blank"
        class="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        {{ t('getApplication') }}
        <ExternalLink :size="16" />
      </a>
      <button
        @click="auth.retry()"
        :disabled="auth.isLoading"
        class="mt-3 block mx-auto text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
      >
        {{ t('appNotInstalledRefresh') }}
      </button>
    </div>
  </div>

  <!-- Error state -->
  <div v-else-if="auth.state === 'error'" class="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
    <div class="text-center max-w-md">
      <!-- A join request waiting for an admin (inside Lark) is not a failure. -->
      <h1 v-if="auth.error === 'authErrorJoinRequested'" class="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"><Clock :size="20" class="shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />{{ t('joinRequestedTitle') }}</h1>
      <h1 v-else class="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2"><AlertCircle :size="20" class="shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />{{ t('authFailed') }}</h1>
      <p class="text-gray-600 dark:text-gray-400 mb-4">{{ authMessage }}</p>
      <button
        @click="auth.retry()"
        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        {{ t('authTryAgain') }}
      </button>
    </div>
  </div>

  <!-- Authenticated - show app. Inside the sidebar shell unless the page opts
       out (the full-screen designer); the public pages above never get it.
       Nothing until the first route has resolved: before that route.meta is
       the empty start location's, and a deep link to the designer would flash
       the sidebar (and its tenant load) first. -->
  <div v-else-if="!route.matched.length" />
  <CompanyShell v-else-if="route.meta.shell !== false">
    <RouterView />
  </CompanyShell>
  <RouterView v-else />

  <!-- Debug console (always available for debugging in Lark) -->
  <DebugConsole />
</template>
